import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { assetConfirm, assetToast } from '@xdocs/i18n';
import { createDocumentInput } from '@xdocs/schemas';
import type { ContentPolicyAlert, CreateDocumentInput, ErrorAlert } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { Badge, Button, Modal, cn, useI18n, useToast } from '@xdocs/ui';
import { optionalProp } from '../../lib/optionalProp.js';
import { useWs } from '../../ws/WsProvider.js';
import { PersonaStep, ReviewStep, SourcesStep, TemplateStep, TopicStep, LengthStep } from './WizardSteps.js';
import { clearGenerateWizardDraft, loadGenerateWizardDraft, saveGenerateWizardDraft } from './generateWizardPersistence.js';
import { useGenerateEstimate } from './useGenerateEstimate.js';
import { useGenerateT } from './useGenerateT.js';
import styles from './GeneratePage.module.css';

const STEP_IDS = ['template', 'topic', 'sources', 'length', 'persona', 'review'] as const;
type StepId = (typeof STEP_IDS)[number];

const STEP_FIELDS: Record<StepId, (keyof CreateDocumentInput)[]> = {
  template: [],
  topic: ['title'],
  sources: [],
  length: ['difficultyLevel', 'language'],
  persona: [],
  review: [],
};

function isContentPolicyAlert(alert: ErrorAlert): alert is ContentPolicyAlert {
  return alert.code === 'CONTENT_POLICY_VIOLATION' && 'violatedTerms' in alert;
}

/**
 * `/generate` (F1/F-Estep/F15 setup half/F48). Wizard: ①template ②topic
 * ③sources ④length&difficulty ⑤persona ⑥review — one shared draft model
 * (a single `CreateDocumentInput` form) across every step; "Back never
 * validates" (only "Next" does, and only the CURRENT step's fields).
 * "Confirm & generate" → `document.create` → `generate.start` (enqueues the
 * near-0-cost `outline-worker`; the F15 chapter-structure gate itself
 * happens on `/documents/:id` once the proposal lands — see that route's
 * open issues for why: no WS op exists yet to fetch outline content).
 */
export function GeneratePage(): ReactElement {
  const { t } = useGenerateT();
  const { locale, t: tCommon } = useI18n();
  const navigate = useNavigate();
  const { wsClient } = useWs();
  const { push: pushToast } = useToast();

  // E20 §5.10(3): resume the step + entered values a reopen/reload left off
  // on, instead of silently discarding wizard progress back to step 1.
  const persistedDraft = useMemo(() => loadGenerateWizardDraft(STEP_IDS.length - 1), []);

  const [stepIndex, setStepIndex] = useState(persistedDraft?.stepIndex ?? 0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ErrorAlert | null>(null);

  const methods = useForm({
    resolver: zodResolver(createDocumentInput),
    mode: 'onBlur',
    defaultValues: {
      title: persistedDraft?.values.title ?? '',
      kind: persistedDraft?.values.kind ?? 'book',
      difficultyLevel: persistedDraft?.values.difficultyLevel ?? 'basic',
      language: persistedDraft?.values.language ?? 'en',
      options: {
        illustrate: persistedDraft?.values.options?.illustrate ?? false,
        ...optionalProp('brief', persistedDraft?.values.options?.brief),
        ...optionalProp('chapterCountTarget', persistedDraft?.values.options?.chapterCountTarget),
        ...optionalProp('sourceFileIds', persistedDraft?.values.options?.sourceFileIds),
      },
      ...optionalProp('templateId', persistedDraft?.values.templateId),
      ...optionalProp('personaId', persistedDraft?.values.personaId),
      ...optionalProp('sourceBookId', persistedDraft?.values.sourceBookId),
    },
  });
  const { trigger, watch } = methods;
  const values = watch();
  const step = STEP_IDS[stepIndex] as StepId;

  // Flips once `document.create`/`generate.start` succeed, so the persist
  // effect below stops re-writing the draft on the render(s) that follow
  // `clearGenerateWizardDraft()` (`setShowConfirm`/toast/navigate state
  // updates all land in the same React 18 batch, which would otherwise race
  // the clear and resurrect the just-submitted draft).
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (hasSubmittedRef.current) return;
    // `values` (from `watch()`) is a fresh object every render — persisting
    // on every entered-values change is exactly E20 §5.10(3)'s contract; no
    // deep-equality dance needed for a form this small.
    saveGenerateWizardDraft({ stepIndex, values });
  }, [stepIndex, values]);

  const { estimate, isLoading: isEstimating, isError: isEstimateError } = useGenerateEstimate({
    kind: values.kind ?? 'book',
    difficultyLevel: values.difficultyLevel ?? 'basic',
    options: { ...values.options, illustrate: values.options?.illustrate ?? false },
  });

  async function goNext(): Promise<void> {
    const fields = STEP_FIELDS[step];
    if (fields.length > 0) {
      const valid = await trigger(fields);
      if (!valid) return;
    }
    setStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
  }

  function goBack(): void {
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  async function handleGenerate(): Promise<void> {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const parsed = createDocumentInput.parse(methods.getValues());
      const created = await requestTyped(wsClient, 'document.create', parsed);
      const accepted = await requestTyped(wsClient, 'generate.start', { documentId: created.id });
      setShowConfirm(false);
      hasSubmittedRef.current = true;
      pushToast(assetToast(locale, 'success', t('generate:toast.generateStarted.action'), created.title));
      clearGenerateWizardDraft();
      void navigate(`/documents/${created.id}`, { state: { jobId: accepted.jobId } });
    } catch (err) {
      setSubmitError(err as ErrorAlert);
    } finally {
      setIsSubmitting(false);
    }
  }

  const insufficientCredits = estimate ? estimate.credits > estimate.balance : false;
  const confirmMessage = assetConfirm(locale, t('generate:step.review.confirmAction'), values.title || t('generate:step.topic.titleLabel'));
  const contentPolicyError = submitError && isContentPolicyAlert(submitError) ? submitError : null;
  const genericSubmitError = submitError && !contentPolicyError ? submitError : null;

  const costChip = (
    <div className={cn(styles['costRow'])}>
      <span className={cn(styles['costLabel'])}>{t('generate:step.review.costLabel')}</span>
      {isEstimating && <span className={cn(styles['costValue'])}>{t('generate:step.review.costLoading')}</span>}
      {!isEstimating && isEstimateError && <span className={cn(styles['costError'])}>{t('generate:step.review.costError')}</span>}
      {!isEstimating && !isEstimateError && estimate && (
        <span className={cn(styles['costValue'])}>{t('generate:unit.credits', { count: estimate.credits })}</span>
      )}
    </div>
  );

  const progressPercent = Math.round(((stepIndex + 1) / STEP_IDS.length) * 100);
  const stepLabel = t('generate:wizard.step', { step: stepIndex + 1, total: STEP_IDS.length });

  return (
    <FormProvider {...methods}>
      <div className={cn(styles['page'])}>
        <div className={cn(styles['topRow'])}>
          <span className={cn(styles['eyebrow'])}>
            {t('generate:wizard.newDocument')} · {t('generate:wizard.stepFraction', { step: stepIndex + 1, total: STEP_IDS.length })}
          </span>
          <button type="button" className={cn(styles['cancelLink'])} onClick={() => void navigate('/documents')}>
            {tCommon('common:action.cancel')}
          </button>
        </div>
        <div
          className={cn(styles['progressTrack'])}
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('generate:wizard.progressLabel')}
        >
          <div className={cn(styles['progressFill'])} style={{ width: `${progressPercent}%` }} />
        </div>
        {/* Mock 19 nests the "STEP n OF m" eyebrow inside the step card,
         * directly above its title (not floating between the progress bar
         * and the card) — pass it down so each step panel renders it as its
         * own first child. */}
        {step === 'template' && <TemplateStep stepLabel={stepLabel} />}
        {step === 'topic' && <TopicStep stepLabel={stepLabel} />}
        {step === 'sources' && <SourcesStep stepLabel={stepLabel} />}
        {step === 'length' && <LengthStep stepLabel={stepLabel} />}
        {step === 'persona' && <PersonaStep stepLabel={stepLabel} />}
        {step === 'review' && <ReviewStep stepLabel={stepLabel} costChip={costChip} onSubmit={() => setShowConfirm(true)} />}

        <div className={cn(styles['nav'])}>
          {stepIndex > 0 ? (
            <Button variant="ghost" size="lg" onClick={goBack}>
              {t('generate:wizard.back')}
            </Button>
          ) : (
            <span />
          )}
          {stepIndex < STEP_IDS.length - 1 && (
            <Button variant="primary" size="lg" onClick={() => void goNext()}>
              {t('generate:wizard.next')}
            </Button>
          )}
        </div>

        {genericSubmitError && (
          <p role="alert" className={cn(styles['submitErrorNotice'])}>
            {genericSubmitError.message}
          </p>
        )}
      </div>

      {/* Content-policy refusal (§0.8) renders INSIDE the still-open confirm
       * modal (not the page behind it) — `<Modal>` marks every other
       * `document.body` child `inert` while open, so a page-level notice
       * would be unreachable/invisible to the very "Edit & retry" flow it
       * needs to offer. */}
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title={t('generate:confirm.title')} size="sm">
        {contentPolicyError ? (
          <div role="alert" className={cn(styles['policyNotice'])}>
            <p>{t('generate:error.contentPolicy.title')}</p>
            <div className={cn(styles['policyChips'])}>
              {contentPolicyError.violatedTerms.map((category) => (
                <Badge key={category} tone="policy">
                  {t(`generate:policyCategory.${category}`)}
                </Badge>
              ))}
            </div>
            <p>{t('generate:error.contentPolicy.notCharged')}</p>
            <div className={cn(styles['policyLinks'])}>
              <Button
                variant="secondary"
                onClick={() => {
                  setSubmitError(null);
                  setShowConfirm(false);
                }}
              >
                {t('generate:error.contentPolicy.cta')}
              </Button>
              <Link to={contentPolicyError.policyUrl}>{t('generate:error.contentPolicy.readPolicy')}</Link>
            </div>
          </div>
        ) : (
          <div className={cn(styles['confirmBody'])}>
            <p>{confirmMessage.message}</p>
            {estimate && (
              <p className={cn(styles['costLabel'])}>
                {t('generate:unit.credits', { count: estimate.credits })} —{' '}
                {t('generate:step.review.balanceLine', { balance: estimate.balance, balanceAfter: estimate.balanceAfter })}
              </p>
            )}
            {insufficientCredits && (
              <div className={cn(styles['insufficientNotice'])}>
                <p>{t('generate:error.insufficientCredits.title')}</p>
                <p>{t('generate:error.insufficientCredits.description')}</p>
                {/* Route not built yet (F57/WS-BILL) — pointer per task spec, 404 is acceptable for now. */}
                <Link to="/pricing/credits">{t('generate:error.insufficientCredits.cta')}</Link>
              </div>
            )}
            <div className={cn(styles['confirmActions'])}>
              <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={isSubmitting}>
                {t('generate:wizard.back')}
              </Button>
              <Button
                variant="primary"
                state={isSubmitting ? 'loading' : 'idle'}
                disabled={insufficientCredits}
                {...(insufficientCredits ? { disabledReason: t('generate:error.insufficientCredits.title') } : {})}
                onClick={() => void handleGenerate()}
              >
                {t('generate:step.review.submit')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </FormProvider>
  );
}
